import realStyled from 'styled-components';
import dayjs from 'dayjs';
import utils from 'helpers/utils';
import {styleSheet} from 'helpers/hydrate';

// Only the default `styled` is wrapped, and only that is exported (plus the recorder's own
// markers below). css, keyframes, createGlobalStyle, ThemeProvider and the rest are untouched by
// the recorder, so they keep coming straight from styled-components - re-exporting them here made
// this module a second front door to the package and, for anything helpers/constants reaches,
// an import cycle waiting to happen.

// Dev-only style-prop recorder.
//
// Every *.styles.js imports its default `styled` from here instead of from styled-components.
// In development each styled component reports the props it was rendered with - the pre-attrs
// input a replay needs to regenerate that component's CSS. In production this is the untouched
// styled-components export, so it costs exactly nothing.
//
// Only our own styles files route through here; MUI keeps importing styled-components
// directly, so being recorded already means the component is one of ours.
//
// The manifest is keyed by $componentName (the name useComponentProps stamps on every
// component - 'Button', 'Tooltip', 'ChipList'), which is also the styles file's name. Generate
// imports that file lazily and renders its default export, so no componentId or registry is
// involved.
//
// The gate is NODE_ENV, not system.isDevelopment(): the latter is also true for the
// production `serve:develop` build, and recording on every render there would invalidate any
// profile taken on it. Recording is a `npm start` activity.
const recording = process.env.NODE_ENV === 'development';

// componentName -> Map(serialisedKey -> propset). One entry per distinct prop shape a
// component was rendered with, deduped by its serialised key.
const manifest = new Map();

// Generate renders each recorded styled component to reinject its css. A styled(OurComponent)
// would render that whole component recursively - which needs context and data a bare theme root
// does not have, and is wasted work since the wrapped component has its own manifest entry. So
// generate passes this prop: a predicate the wrapper calls with the styled target. When it
// returns true (the target is one of ours, or on generate's exclude list) the component renders
// `as: 'div'` - the css is still
// generated, the recursion is not. MUI / DOM targets fail the predicate and render normally.
// It is transient ($-prefixed, never forwarded to the DOM) and skipped from the manifest.
export const GENERATE_PROP = '$__generate__';

// Props that never belong in the captured input. theme and the .attrs output (style, $css)
// regenerate on replay; a ref cannot be serialised; $componentName and $modifiers are what
// useComponentProps reads to build the className upstream - the styled component never reads
// them, so they are pure noise in the manifest.
const SKIP = new Set(['theme', 'style', '$css', 'ref', 'forwardedRef', '$componentName', '$modifiers', GENERATE_PROP]);

// A function or element prop is not serialisable, but its presence is a guard - MUI forks
// ownerState on onDelete / onClick (clickable) and on startIcon / avatar / deleteIcon presence,
// and our own templates read the same. Each is stored as its marker, and replay restores a
// no-op function / a placeholder element for any prop holding it. Reserved enough that a real
// string prop cannot collide.
export const FUNCTION_PROP = ' fn';
export const ELEMENT_PROP = ' el';
export const DATE_PROP = ' date';

// A reference-unique "omit this value" signal - null cannot serve, it is a real prop value.
const DROP = {};

// A React component type passed as a prop (a forwardRef / memo object like TransitionComponent)
// is not an element and not a style input - it serialises to garbage, so it is dropped.
function isReactObject (value) {
  return utils.isObject(value) && Boolean(value.$$typeof);
}

// A Date or a dayjs. utils.isDate is `instanceof Date`, which a dayjs is not - it is a plain
// object of primitives, so the structural walk below happily called it serialisable and the
// capture's JSON.stringify then ran its toJSON and wrote an ISO string. Replay handed that string
// straight to AdapterDayjs, which called .isValid on it and threw. Only presence matters for css
// either way, so both become DATE_PROP and replay inserts a live date.
//
// Top level only, deliberately. A date nested inside a data array (recharts' `data` / `points`,
// a collection's `data`) is content rather than a style input, its consumers read the ISO string
// happily, and restore() only walks the top level anyway - so refusing it here would drop the
// whole array and lose everything those components generate from it.
function isTemporal (value) {
  return utils.isDate(value) || dayjs.isDayjs(value);
}

// True only if the value is pure JSON data - primitives, and plain arrays / objects all the way
// down. A JSON.stringify does NOT prove this: it silently drops a function or a Symbol, so a
// slots / slotProps object holding a component (a memo / forwardRef with a $$typeof Symbol)
// stringifies without throwing to a mangled {} that replays as an invalid element. So the check
// is a structural walk: a nested function or React object makes the whole value unserialisable,
// and a `seen` set makes it cycle-safe (a DOM node reached through e.g. anchorEl is dropped, not
// recursed into forever).
function isSerialisable (value, seen = new Set()) {
  let serialisable = true;
  if (utils.isFunction(value) || isReactObject(value)) {
    serialisable = false;
  } else if (utils.isArray(value) || utils.isObject(value)) {
    if (seen.has(value)) {
      serialisable = false;
    } else {
      seen.add(value);
      const values = utils.isArray(value) ? value : Object.keys(value).map((key) => {
        return value[key];
      });
      serialisable = values.every((nested) => {
        return isSerialisable(nested, seen);
      });
    }
  }
  return serialisable;
}

function serialiseValue (value) {
  let result = DROP;
  if (utils.isFunction(value)) {
    result = FUNCTION_PROP;
  } else if (utils.isReactElement(value)) {
    result = ELEMENT_PROP;
  } else if (isTemporal(value)) {
    // a Date or a dayjs JSON-serialises to an ISO string, which replays as a string a date
    // adapter rejects. Store the marker and let replay insert a live date instead - only its
    // presence matters.
    result = DATE_PROP;
  } else if (isReactObject(value)) {
    result = DROP;
  } else if (isSerialisable(value)) {
    result = value;
  }
  return result;
}

function serialise (props) {
  return Object.keys(props).reduce((result, key) => {
    if (key === 'children') {
      // only presence is ever read by a guard - replay passes a placeholder child
      result.children = Boolean(props.children);
    } else if (!SKIP.has(key)) {
      const value = serialiseValue(props[key]);
      if (value !== DROP) {
        result[key] = value;
      }
    }
    return result;
  }, {});
}

// A propset is only worth keeping if rendering it actually injected new CSS - two $maxWidth
// values that differ but feed the same CSS variable produce identical css and the second
// inserts nothing. styled-components inserts synchronously during a component's generate,
// right after our recording .attrs set `pending` and in the same render, so an insert means
// exactly this component produced new css. commit() dedupes by serialised key.
let pending = null;

function commit (name, propset) {
  if (name) {
    const key = JSON.stringify(propset);
    const found = manifest.get(name) ?? new Map();
    if (!found.has(key)) {
      found.set(key, propset);
      manifest.set(name, found);
    }
  }
}

if (recording) {
  const insertRules = styleSheet.insertRules.bind(styleSheet);
  styleSheet.insertRules = (id, cssName, rules) => {
    if (pending) {
      // serialise here, not in the .attrs: only a render that injects new css reaches this,
      // so the cost is paid once per variant instead of on every styled render app-wide. The
      // insert is synchronous within the same generate, so pending.props is still this render's
      commit(pending.name, serialise(pending.props));
      pending = null;
    }
    return insertRules(id, cssName, rules);
  };
}

// The captured manifest, plain and serialisable: {componentName: [propset, ...]}.
export function recordedProps () {
  const result = {};
  manifest.forEach((propsets, name) => {
    result[name] = [...propsets.values()];
  });
  return result;
}

export function clearRecordedProps () {
  manifest.clear();
}

// Non-intrusive recording: the component styled-components builds is the native one, with its
// identity, tree and behaviour untouched. A recording .attrs is prepended before the styles
// file's own attrs, so it runs first on every render and sees the pre-attrs input. It reads
// $componentName as the manifest key and records the props into `pending`; the insertRules
// hook commits them if that render injected css. Returning {} changes no prop, and the styles
// file's own .attrs / .withConfig / template are the native ones - nothing else is wrapped.
function recordingAttrs (props, wrapped) {
  pending = {name: props.$componentName, props};
  let attrs = {};
  const predicate = props[GENERATE_PROP];
  if (utils.isFunction(predicate) && predicate(wrapped)) {
    // generate replaces our own wrapped component with a plain div: the css still generates,
    // but the component itself (which would need context/data) is not rendered.
    attrs = {as: 'div'};
  }
  return attrs;
}

// `wrapped` is the styled target - the Component in styled(Component) or the tag in styled.div -
// captured so the generate predicate can test it by reference against our component set.
function instrument (factory, wrapped) {
  return factory.attrs((props) => {
    return recordingAttrs(props, wrapped);
  });
}

// A Proxy covers every entry form uniformly: apply -> styled(Component) / styled(fn), get ->
// styled.div and the other tag factories. Non-function property reads pass straight through.
const recordingStyled = new Proxy(realStyled, {
  apply: (target, thisArg, args) => {
    return instrument(target(...args), args[0]);
  },
  get: (target, prop) => {
    const value = target[prop];
    if (typeof prop === 'string' && typeof value === 'function') {
      return instrument(value, prop);
    } else {
      return value;
    }
  }
});

const styled = recording ? recordingStyled : realStyled;

export default styled;
